// @flow
import React from 'react';
import moment from 'moment';
import { get, filter, isEmpty, isNil, toNumber } from 'lodash';
import { Modal, DayPickerInput, Input, InputMask, Toggle, Button, Icon } from '@elements';
import { FormFooterModal, PlatformSwitch } from '@blocks';
import { withForm } from '@hocs';
import { isPresent, createNsTranslator, fSufixTwoPoints, handleSubmitEvent } from '@helpers/helpers';
import { withTranslation } from 'react-i18next';
import { currency } from '../../../../elements/FormattedText/formatters';
import ServiceService, { QUANTITY_INPUT_PATTERN } from '../../../../../services/ServiceService';
import './ServiceFormModal.scss';

type Props = {
  i18n: Object,
  onClose: Function,
  formData: Object,
  touchedFields: Object,
  handleFormChange: Function,
  handleTouchField: Function,
  bindValidation: Function,
  formChange: Function,
  onDelete: Function,
  onSave: Function,
  onSetConfirmOnClose: Function,
  formEdited: Function,
  isNewTemplate: boolean,
  servicesByQuantity: boolean,
  t: Function,
};

type State = {
  errors: { [field: string]: string },
}


class ServiceFormModal extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      errors: {},
    };
    this._ = createNsTranslator('financial', this.props.t);
  }

  componentDidMount() {
    const { bindValidation } = this.props;
    bindValidation(this.handleValidation);
    handleSubmitEvent('#serviceFormModalFormElement', this.handleSave);
  }

  componentWillReceiveProps(next: Props) {
    this._ = createNsTranslator('financial', next.t);
  }

  renderQuantityFields = (isHealthInsurance: boolean) => {
    const { formData, handleTouchField } = this.props;
    const { _ } = this;

    return (
      <React.Fragment>
        <Input
          name="quantity"
          label={_('label/quantity', 'global', fSufixTwoPoints)}
          inputMode="decimal"
          disabled={isHealthInsurance}
          unit={ServiceService.unitLabel(formData.unit, ServiceService.parseQuantity(formData.quantity))}
          value={get(formData, 'quantity', '')}
          onChange={this.handleQuantityChange}
          onBlur={this.handleQuantityBlur}
          error={get(this.state.errors, 'quantity', null)}
        />
        <div className="serviceUnitPrice">
          <InputMask
            type="currency"
            name="unitPrice"
            label={_('label/unit_price', null, fSufixTwoPoints)}
            disabled={isHealthInsurance}
            onChange={this.handleUnitPriceChange}
            onBlur={handleTouchField}
            value={get(formData, 'unitPrice', '')}
          />
        </div>
      </React.Fragment>
    );
  };

  render() {
    const {
      onClose, handleFormChange, handleTouchField, formData, onDelete,
      isNewTemplate,
    } = this.props;
    const { _ } = this;
    const { errors } = this.state;

    const title = !isNewTemplate ? _('label/edit_service') : _('action/new_service');

    const isHealthInsurance = formData.status === 'health_insurance';
    const byQuantity = this.isByQuantity();

    const date = moment(get(formData, 'date', ''), 'YYYY-MM-DD');

    const content = (
      <React.Fragment>
        <form className="form">
          <Input
            name="name"
            label={_('label/service', 'global', fSufixTwoPoints)}
            value={get(formData, 'name', '')}
            onChange={handleFormChange}
            onBlur={handleTouchField}
            disabled={!isNewTemplate}
            error={get(errors, 'name', null)}
            autofocus
          />
          {byQuantity && this.renderQuantityFields(isHealthInsurance)}
          <InputMask
            type="currency"
            name="price"
            label={byQuantity ? _('label/total_price', null, fSufixTwoPoints) : _('label/value', 'global', fSufixTwoPoints)}
            disabled={isHealthInsurance}
            onChange={this.handleNumbersFormChange}
            onBlur={handleTouchField}
            error={get(errors, 'price', null)}
            hint={byQuantity ? this.discountHint() : undefined}
            value={get(formData, 'price', '')}
          />
          <br />
          {!isNewTemplate &&
            <DayPickerInput
              name="date"
              label={_('label/date', 'global', fSufixTwoPoints)}
              onSelect={handleFormChange}
              onBlur={handleTouchField}
              error={get(errors, 'date', null)}
              disabled={!get(formData, 'allowEditDate', false)}
              date={date}
            />
          }
          &nbsp;
          {!isNewTemplate &&
            <Input
              name="notes"
              label={_('label/notes', 'global', fSufixTwoPoints)}
              onChange={handleFormChange}
              onBlur={handleTouchField}
              error={get(errors, 'notes', null)}
              value={get(formData, 'notes', '')}
            />
          }
          {isNewTemplate &&
            <Toggle
              name="healthInsurance"
              key="toggleSwitch"
              label={_('label/status_health_insurance', 'global')}
              onChange={handleFormChange}
              isChecked={get(formData, 'healthInsurance')}
            />
          }
        </form>
      </React.Fragment>
    );

    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <Modal onClose={onClose}>
            <div className="modalHeader">
              <span className="modalTitle">{title}</span>
            </div>
            <div className="modalGrid">
              <div className="modalContainer">
                <div className="modalContent greyBg" id="serviceFormModalFormElement">
                  {content}
                </div>
                <FormFooterModal
                  onSave={this.handleSave}
                  onCancel={onClose}
                  onDelete={!isNewTemplate ? onDelete : null}
                />
              </div>
            </div>
          </Modal>
        )}
        mobile={() => (
          <Modal id="ServiceFormModal" className="asView" onClose={onClose}>
            <div className="modalHeader">
              <Button onClick={onClose}>
                <Icon iconName="close" className="icon" />
              </Button>
              <span className="title">{title}</span>
              <Button className="primary" text={_('action/save', 'global')} onClick={this.handleSave} />
            </div>
            <div className="modalGrid">
              <div className="modalContainer">
                <div className="modalContent greyBg">
                  {content}
                  {!isNewTemplate && (
                    <div className="formDeleteAction">
                      <Button className="dangerOutline" text={_('action/delete_service')} onClick={onDelete} />
                    </div>
                  )}
                </div>
              </div>

            </div>
          </Modal>
        )}
      />
    );
  }

  _: Function;

  isByQuantity = () => !!this.props.servicesByQuantity && ServiceService.isByQuantity(this.props.formData);

  // "Desconto de R$ 194,00 sobre 2 × R$ 747,00" when the total differs from quantity × unit price
  discountHint = () => {
    const { formData, t, i18n } = this.props;
    const quantity = ServiceService.parseQuantity(formData.quantity);
    const unitPrice = toNumber(get(formData, 'unitPrice', 0));
    const difference = ServiceService.discount(quantity, unitPrice, get(formData, 'price', 0));
    if (!(quantity > 0) || Math.abs(difference) < 0.01) return undefined;

    return t(difference > 0 ? 'financial:label/discount_hint' : 'financial:label/surcharge_hint', {
      amount: currency(Math.abs(difference), i18n.language),
      quantity: ServiceService.formatQuantity(quantity),
      unitPrice: currency(unitPrice, i18n.language),
    });
  };

  discountOf = (data: Object) => ServiceService.discount(ServiceService.parseQuantity(data.quantity), toNumber(data.unitPrice), data.price);

  // The discount (or surcharge) the sale has, in reais. Taken from the sale as saved on the first change, and from
  // the total whenever it's typed: the intermediate states of a quantity being retyped must not reset it.
  discountGiven: ?number;

  // A total typed while the quantity was empty stands while the quantity is typed ("5", "55"); the discount comes
  // from it when the quantity field is left, or the unit price changes
  totalTypedWithoutQuantity: boolean;

  // Changing quantity or unit price recomputes the total, keeping the discount already given
  retotal = (changes: Object) => {
    const { formData } = this.props;
    if (this.totalTypedWithoutQuantity) {
      // Until there's a quantity to settle it with, the typed total stands, whatever else changes
      if (!('unitPrice' in changes) || !ServiceService.isValidQuantity(formData.quantity)) {
        this.props.handleFormChange({ _isComposedChangeEvent: true, composedValue: changes });
        return;
      }
      this.settleTypedTotal();
    }
    if (isNil(this.discountGiven)) this.discountGiven = this.discountOf(formData);
    const { quantity, unitPrice } = { ...formData, ...changes };
    const gross = ServiceService.lineTotal(ServiceService.parseQuantity(quantity), unitPrice);
    const price = Math.max(Math.round((gross - toNumber(this.discountGiven)) * 100) / 100, 0);
    this.props.handleFormChange({
      _isComposedChangeEvent: true,
      composedValue: { ...changes, price: String(price) },
    });
  };

  handleQuantityChange = ({ value }: Object) => QUANTITY_INPUT_PATTERN.test(value) && this.retotal({ quantity: value });

  handleUnitPriceChange = ({ value }: Object) => this.retotal({ unitPrice: String(value) });

  handleQuantityBlur = (event: Object) => {
    if (this.totalTypedWithoutQuantity) this.settleTypedTotal();
    this.props.handleTouchField(event);
  };

  settleTypedTotal = () => {
    const { formData } = this.props;
    if (!ServiceService.isValidQuantity(formData.quantity)) return;
    this.totalTypedWithoutQuantity = false;
    this.discountGiven = this.discountOf(formData);
  };

  handleNumbersFormChange = (data) => {
    const value = String(data.value).replace(/[^\d,.-]/g, '');
    if (data.name === 'price' && this.isByQuantity()) {
      const { formData } = this.props;
      this.totalTypedWithoutQuantity = !ServiceService.isValidQuantity(formData.quantity);
      if (!this.totalTypedWithoutQuantity) this.discountGiven = this.discountOf({ ...formData, price: value });
    }
    this.props.formChange(data.name, value);
  };

  handleValidation = (isSubmit: boolean = false) => new Promise(resolve => {
    const {
      formData, touchedFields, formEdited, onSetConfirmOnClose,
      isNewTemplate,
    } = this.props;
    const price = formData.price === null || formData.price === undefined ? -1 : toNumber(formData.price);
    const errors = {};

    if (isNewTemplate) {
      if (!isPresent(formData.name) && (touchedFields.name || isSubmit)) {
        errors.name = this._('label/error/name');
      }
    }

    if (!isNewTemplate) {
      if (!moment(formData.date).isValid() && (touchedFields.date || isSubmit)) {
        errors.date = this._('label/error/date');
      }
    }

    if (price < 0 && (touchedFields.price || isSubmit)) {
      errors.price = this._('label/error/value');
    }

    if (this.isByQuantity() && !ServiceService.isValidQuantity(formData.quantity) && (touchedFields.quantity || isSubmit)) {
      errors.quantity = this._('label/error/quantity');
    }

    onSetConfirmOnClose(formEdited());

    this.setState({ errors }, resolve);
  });

  handleSave = () => this.handleValidation(true).then(() => {
    const { onSave, formData } = this.props;
    if (!this.haveErrors()) {
      onSave({
        ...formData,
        price: toNumber(formData.price) || 0,
        ...(ServiceService.isByQuantity(formData) ? {
          quantity: ServiceService.parseQuantity(formData.quantity),
          unitPrice: toNumber(formData.unitPrice) || 0,
        } : {}),
      });
    }
  });

  haveErrors = () => filter(this.state.errors, item => !isEmpty(item)).length > 0;
}


const initialData = {
  price: null,
};

export default withForm(initialData)(withTranslation()(ServiceFormModal));
